import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PurchaseRequestDetailSchema,
  SupplierSchema,
  type AssetClassDto,
  type EmployeeDto,
  type PurchaseRequestDetailDto,
  type SupplierDto,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { ApiError, getData, newIdempotencyKey, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { ListSelect } from '../crm/common';
import { RecordFailure } from '../finance/detail';
import { EmployeePicker, SupplierPicker } from '../finance/lookups';
import {
  AssetClassPicker,
  EDITABLE,
  emptyTarget,
  loadAssetClass,
  loadTarget,
  PR_API,
  PR_TYPE,
  TargetFields,
  targetPayload,
  targetProblems,
  TaxCodeSelect,
  useBusinessToday,
  useProcurementLists,
  type TargetDraft,
} from './purchasing';

/** One requisition line as the editor holds it (FRD-PRC-018 to 020, PRC-026). */
interface LineDraft {
  readonly key: number;
  description: string;
  quantity: string;
  estimatedUnitCost: string;
  requiredByDate: string;
  categoryCode: string;
  isAsset: boolean;
  assetClass: AssetClassDto | null;
  target: TargetDraft;
  taxCodeId: string;
  budgetReference: string;
}

const emptyLine = (key: number, requiredByDate: string): LineDraft => ({
  key,
  description: '',
  quantity: '',
  estimatedUnitCost: '',
  requiredByDate,
  categoryCode: '',
  isAsset: false,
  assetClass: null,
  target: emptyTarget(),
  taxCodeId: '',
  budgetReference: '',
});

const DECIMAL = /^\d{1,15}(\.\d{1,4})?$/;

function lineProblems(lines: readonly LineDraft[]): string[] {
  return lines.flatMap((line, index) => {
    const n = `Line ${String(index + 1)}`;
    const out: string[] = [];
    if (line.description.trim() === '') out.push(`${n}: describe the item or service.`);
    if (!DECIMAL.test(line.quantity.trim()) || /^0*(\.0*)?$/.test(line.quantity.trim())) {
      out.push(`${n}: enter a quantity above zero (up to 4 decimals).`);
    }
    if (!DECIMAL.test(line.estimatedUnitCost.trim())) {
      out.push(`${n}: enter the estimated unit cost (up to 4 decimals).`);
    }
    if (line.requiredByDate === '') out.push(`${n}: enter the required-by date.`);
    if (line.isAsset && line.assetClass === null) {
      out.push(`${n}: choose the asset class of this asset line.`);
    }
    out.push(...targetProblems(line.target, n));
    return out;
  });
}

function linePayload(line: LineDraft) {
  return {
    description: line.description.trim(),
    quantity: line.quantity.trim(),
    estimatedUnitCost: line.estimatedUnitCost.trim(),
    requiredByDate: line.requiredByDate,
    isAsset: line.isAsset,
    ...(line.isAsset && line.assetClass !== null ? { assetClassId: line.assetClass.id } : {}),
    ...(line.categoryCode !== '' ? { categoryCode: line.categoryCode } : {}),
    ...(line.taxCodeId !== '' ? { taxCodeId: line.taxCodeId } : {}),
    ...(line.budgetReference.trim() !== '' ? { budgetReference: line.budgetReference.trim() } : {}),
    ...targetPayload(line.target),
  };
}

/** `/procurement/purchase-requests/new`: raise a request, for oneself by default (FRD-PRC-021). */
export function NewPurchaseRequestPage() {
  return <RequestEditor existing={null} />;
}

/** `/procurement/purchase-requests/:id/edit`: change a draft, returned or rejected request. */
export function EditPurchaseRequestPage() {
  const { id = '' } = useParams();
  const request = useQuery({
    queryKey: ['procurement', 'purchase-request', id],
    queryFn: () => getData(`${PR_API}/${id}`, PurchaseRequestDetailSchema),
  });
  const back = (
    <Button
      component={RouterLink}
      to={`/procurement/purchase-requests/${id}`}
      startIcon={<ArrowBackIcon />}
    >
      Back to the request
    </Button>
  );
  if (request.isPending) return <LoadingState label="Loading the purchase request" />;
  if (request.isError) {
    return (
      <RecordFailure
        title="Purchase request"
        noun="purchase request"
        error={request.error}
        back={back}
      />
    );
  }
  if (!EDITABLE.includes(request.data.request.status)) {
    return (
      <Stack spacing={3}>
        <PageHeader title={`Edit ${request.data.request.documentNo}`} actions={back} />
        <Alert severity="info">
          A {request.data.request.status} purchase request cannot be edited.
        </Alert>
      </Stack>
    );
  }
  return <RequestEditor existing={request.data} />;
}

/**
 * The full-page multi-line editor (FRD-PRC-017 to 020). The API validates everything again —
 * active supplier, account usability, the dimensions the account requires, the asset class — and
 * its refusal is shown as it is.
 */
function RequestEditor({ existing }: { existing: PurchaseRequestDetailDto | null }) {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lists = useProcurementLists();
  const today = useBusinessToday();
  const [requester, setRequester] = useState<EmployeeDto | null>(null);
  const [requestDate, setRequestDate] = useState(existing?.request.requestDate ?? today);
  const [justification, setJustification] = useState(existing?.request.justification ?? '');
  const [supplier, setSupplier] = useState<SupplierDto | null>(null);
  const [lines, setLines] = useState<LineDraft[]>(() =>
    existing === null ? [emptyLine(1, '')] : [],
  );
  const [loading, setLoading] = useState(existing !== null);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  // An existing request's lines are rebuilt from their masters, where the session may read them.
  useEffect(() => {
    if (existing === null) return;
    const run = { cancelled: false };
    void (async () => {
      const rebuilt: LineDraft[] = [];
      for (const [index, line] of existing.lines.entries()) {
        rebuilt.push({
          key: index + 1,
          description: line.description,
          quantity: line.quantity,
          estimatedUnitCost: line.estimatedUnitCost,
          requiredByDate: line.requiredByDate,
          categoryCode: line.categoryCode ?? '',
          isAsset: line.isAsset,
          assetClass: await loadAssetClass(line.assetClassId, can),
          target: await loadTarget(line, can),
          taxCodeId: line.taxCodeId ?? '',
          budgetReference: line.budgetReference ?? '',
        });
      }
      const preferred =
        existing.request.preferredSupplierId === null || !can('procurement.supplier:view')
          ? null
          : await getData(
              `/api/v1/procurement/suppliers/${existing.request.preferredSupplierId}`,
              SupplierSchema,
            ).catch(() => null);
      if (!run.cancelled) {
        setLines(rebuilt);
        setSupplier(preferred);
        setLoading(false);
      }
    })();
    return () => {
      run.cancelled = true;
    };
    // Rebuilt once per loaded version of the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.request.id, existing?.request.version]);

  const problems = lineProblems(lines);
  const complete = requestDate !== '' && justification.trim() !== '' && lines.length > 0;
  const body = {
    requestDate,
    justification: justification.trim(),
    ...(supplier !== null ? { preferredSupplierId: supplier.id } : {}),
    lines: lines.map(linePayload),
  };
  const save = useMutation({
    mutationFn: () =>
      existing === null
        ? postJson(
            PR_API,
            { ...body, ...(requester !== null ? { requesterEmployeeId: requester.id } : {}) },
            PurchaseRequestDetailSchema,
            session.data?.csrfToken ?? '',
            { idempotencyKey },
          )
        : patchJson(
            `${PR_API}/${existing.request.id}`,
            body,
            PurchaseRequestDetailSchema,
            session.data?.csrfToken ?? '',
            existing.request.version,
          ),
    onSuccess: async (saved) => {
      setIdempotencyKey(newIdempotencyKey());
      await queryClient.invalidateQueries({ queryKey: ['procurement'] });
      await navigate(`/procurement/purchase-requests/${saved.request.id}`);
    },
  });
  const update = (key: number, patch: Partial<LineDraft>) => {
    setLines((current) => current.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };
  const nextKey = lines.reduce((max, l) => Math.max(max, l.key), 0) + 1;
  const title = existing === null ? 'New purchase request' : `Edit ${existing.request.documentNo}`;
  const back = (
    <Button
      component={RouterLink}
      to={
        existing === null
          ? '/procurement/purchase-requests'
          : `/procurement/purchase-requests/${existing.request.id}`
      }
      startIcon={<ArrowBackIcon />}
    >
      {existing === null ? 'All purchase requests' : 'Back to the request'}
    </Button>
  );
  if (loading) return <LoadingState label="Loading the request lines" />;
  if (existing === null && !can(`${PR_TYPE}:create`)) {
    return (
      <Stack spacing={3}>
        <PageHeader title={title} actions={back} />
        <Alert severity="info">You do not have permission to raise purchase requests.</Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <PageHeader
        title={title}
        description="Each line records what is needed, its estimated cost, and where it will be charged."
        actions={back}
      />
      <Box
        component="form"
        aria-label={title}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && problems.length === 0 && !save.isPending) save.mutate();
        }}
      >
        <Stack spacing={3}>
          <Section title="Request">
            <Stack spacing={2} sx={{ p: 2.5 }}>
              {existing === null && can('hr.employee:view') && (
                <EmployeePicker
                  label="Requester"
                  value={requester}
                  onChange={setRequester}
                  helperText="Leave empty to raise the request for yourself"
                />
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  size="small"
                  type="date"
                  label="Request date"
                  required
                  value={requestDate}
                  slotProps={{ inputLabel: { shrink: true } }}
                  onChange={(e) => {
                    setRequestDate(e.target.value);
                  }}
                />
                <Box sx={{ flexGrow: 1, minWidth: 260 }}>
                  {can('procurement.supplier:view') ? (
                    <SupplierPicker
                      value={supplier}
                      onChange={setSupplier}
                      required={false}
                      helperText="Optional. Only an active supplier can be chosen."
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Choosing a preferred supplier needs permission to view suppliers.
                    </Typography>
                  )}
                </Box>
              </Stack>
              <TextField
                size="small"
                label="Justification"
                required
                multiline
                minRows={2}
                value={justification}
                onChange={(e) => {
                  setJustification(e.target.value);
                }}
              />
            </Stack>
          </Section>
          <Section title="Lines">
            <Stack spacing={2} sx={{ p: 2.5 }}>
              {lines.map((line, index) => {
                const n = String(index + 1);
                return (
                  <Box
                    key={line.key}
                    role="group"
                    aria-label={`Line ${n}`}
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}
                  >
                    <Stack
                      direction="row"
                      sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
                    >
                      <Typography variant="subtitle2">Line {n}</Typography>
                      {lines.length > 1 && (
                        <IconButton
                          size="small"
                          aria-label={`Remove line ${n}`}
                          onClick={() => {
                            setLines((current) => current.filter((l) => l.key !== line.key));
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    <Stack spacing={2}>
                      <TextField
                        size="small"
                        label="Item or service"
                        required
                        value={line.description}
                        onChange={(e) => {
                          update(line.key, { description: e.target.value });
                        }}
                      />
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 2,
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
                        }}
                      >
                        <TextField
                          size="small"
                          label="Quantity"
                          required
                          value={line.quantity}
                          onChange={(e) => {
                            update(line.key, { quantity: e.target.value });
                          }}
                        />
                        <TextField
                          size="small"
                          label="Estimated unit cost (AED)"
                          required
                          value={line.estimatedUnitCost}
                          onChange={(e) => {
                            update(line.key, { estimatedUnitCost: e.target.value });
                          }}
                        />
                        <TextField
                          size="small"
                          type="date"
                          label="Required by"
                          required
                          value={line.requiredByDate}
                          slotProps={{ inputLabel: { shrink: true } }}
                          onChange={(e) => {
                            update(line.key, { requiredByDate: e.target.value });
                          }}
                        />
                        <ListSelect
                          label="Category"
                          values={lists.data?.prCategory}
                          value={line.categoryCode}
                          current={line.categoryCode}
                          onChange={(categoryCode) => {
                            update(line.key, { categoryCode });
                          }}
                        />
                      </Box>
                      <TargetFields
                        target={line.target}
                        onChange={(target) => {
                          update(line.key, { target });
                        }}
                      />
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={line.isAsset}
                              onChange={(e) => {
                                update(line.key, {
                                  isAsset: e.target.checked,
                                  assetClass: e.target.checked ? line.assetClass : null,
                                });
                              }}
                            />
                          }
                          label="Asset line"
                        />
                        {line.isAsset && (
                          <Box sx={{ flexGrow: 1, minWidth: 240 }}>
                            <AssetClassPicker
                              value={line.assetClass}
                              required
                              disabled={!can('assets.asset_class:view')}
                              helperText={
                                can('assets.asset_class:view')
                                  ? undefined
                                  : 'Needs permission to view asset classes'
                              }
                              onChange={(assetClass) => {
                                update(line.key, { assetClass });
                              }}
                            />
                          </Box>
                        )}
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TaxCodeSelect
                          value={line.taxCodeId}
                          onChange={(taxCodeId) => {
                            update(line.key, { taxCodeId });
                          }}
                        />
                        <TextField
                          size="small"
                          label="Budget reference"
                          value={line.budgetReference}
                          sx={{ flexGrow: 1 }}
                          onChange={(e) => {
                            update(line.key, { budgetReference: e.target.value });
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
              <Box>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setLines((current) => [...current, emptyLine(nextKey, '')]);
                  }}
                >
                  Add line
                </Button>
              </Box>
            </Stack>
          </Section>
          {problems.length > 0 && (
            <Alert severity="info" variant="outlined">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Alert>
          )}
          {save.isError && (
            <Alert severity="error" variant="outlined">
              {save.error.message}
              {save.error instanceof ApiError && save.error.fieldErrors.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {save.error.fieldErrors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={!complete || problems.length > 0 || save.isPending}
            >
              {save.isPending ? 'Saving…' : existing === null ? 'Save as draft' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
